// client/src/components/EditPOI.js
import React, { useState, useRef, useEffect, useMemo } from 'react';
import IconUpload from './IconUpload';
import { padHexColour, hexToInt, fetchJSON } from './EditPOI_parts/EditPOI_utils';
import EditPOIMainForm from './EditPOI_parts/EditPOI_MainForm';
import EditPOIMoveCategoryModal from './EditPOI_parts/EditPOI_MoveCategoryModal';
import EditPOIMoveTypeModal from './EditPOI_parts/EditPOI_MoveTypeModal';
import EditPOIImageUpload from './EditPOI_parts/EditPOI_ImageUpload';

function safeStr(v) {
  if (v == null) return '';
  return String(v);
}

function parseJsonMaybe(v, fallback) {
  if (v == null) return fallback;
  if (typeof v === 'object') return v;
  const s = String(v).trim();
  if (!s) return fallback;
  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
}

function normalizePoiLayersFromStylesRow(row) {
  const candidates = [
    row?.poi_layers,
    row?.poiLayers,
    row?.poi_layers_json,
    row?.poiLayersJson,
    row?.poi_layer_config,
    row?.poiLayerConfig,
    row?.poi_layer_settings,
    row?.poiLayerSettings,
  ];

  let raw = null;
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') {
      raw = c;
      break;
    }
  }

  const parsed = parseJsonMaybe(raw, []);
  const arr = Array.isArray(parsed) ? parsed : [];

  const input = [];
  for (let i = 0; i < arr.length; i++) {
    const it = arr[i];
    if (it == null) continue;

    if (typeof it === 'string' || typeof it === 'number') {
      const label = String(it).trim();
      if (!label) continue;
      input.push({
        idRaw: null,
        label,
        is_base: i === 0 ? 1 : 0,
        order: i,
      });
      continue;
    }

    if (typeof it === 'object' && !Array.isArray(it)) {
      const idRaw = it.id ?? it.key ?? it.layer_id ?? it.layerId ?? null;
      const labelRaw = it.label ?? it.name ?? it.title ?? it.layer_name ?? it.layerName ?? null;

      const label = labelRaw == null ? '' : String(labelRaw).trim();

      const isBase =
        it.is_base === 1 ||
        it.is_base === true ||
        it.isBase === 1 ||
        it.isBase === true ||
        it.base === 1 ||
        it.base === true ||
        i === 0;

      const order = Number.isFinite(Number(it.order)) ? Number(it.order) : i;

      input.push({
        idRaw: idRaw == null ? null : String(idRaw).trim(),
        label,
        is_base: isBase ? 1 : 0,
        order,
      });
    }
  }

  if (input.length === 0) {
    return [{ id: '0', label: 'Base layer', order: 0, aliases: ['base', 'base layer', 'baselayer'] }];
  }

  let baseIndex = input.findIndex(x => x && (x.is_base === 1 || x.is_base === true));
  if (baseIndex < 0) baseIndex = 0;

  const baseLabel = input[baseIndex] && input[baseIndex].label ? String(input[baseIndex].label).trim() : '';
  const baseAliases = new Set(['0', 'base', 'base layer', 'baselayer']);
  if (input[baseIndex]?.idRaw) baseAliases.add(String(input[baseIndex].idRaw).trim().toLowerCase());

  const others = input
    .filter((_, idx) => idx !== baseIndex)
    .map(x => ({
      label: x && x.label ? String(x.label).trim() : '',
      order: Number.isFinite(Number(x && x.order)) ? Number(x.order) : 0,
      idRaw: x && x.idRaw ? String(x.idRaw).trim() : null,
    }))
    .filter(x => x.label);

  others.sort((a, b) => {
    const ao = Number(a?.order ?? 0);
    const bo = Number(b?.order ?? 0);
    if (ao !== bo) return ao - bo;
    return String(a?.label || '').localeCompare(String(b?.label || ''), undefined, { sensitivity: 'base' });
  });

  const out = [];
  out.push({
    id: '0',
    label: baseLabel || 'Base layer',
    order: 0,
    aliases: Array.from(baseAliases),
  });

  for (let i = 0; i < others.length; i++) {
    const id = String(i + 1);
    const aliases = new Set([id]);
    if (others[i]?.label) aliases.add(String(others[i].label).trim().toLowerCase());
    if (others[i]?.idRaw) aliases.add(String(others[i].idRaw).trim().toLowerCase());
    out.push({
      id,
      label: String(others[i].label).trim(),
      order: i + 1,
      aliases: Array.from(aliases),
    });
  }

  return out;
}

function resolveLayerIdFromValue(value, layers) {
  const raw = safeStr(value).trim();
  if (!raw) return '';

  const lower = raw.toLowerCase();
  const arr = Array.isArray(layers) ? layers : [];

  for (const l of arr) {
    const id = safeStr(l?.id).trim();
    if (!id) continue;
    if (id === raw) return id;
  }

  for (const l of arr) {
    const id = safeStr(l?.id).trim();
    if (!id) continue;
    if (safeStr(l?.id).trim().toLowerCase() === lower) return id;
  }

  for (const l of arr) {
    const id = safeStr(l?.id).trim();
    if (!id) continue;
    const labelLower = safeStr(l?.label).trim().toLowerCase();
    if (labelLower && labelLower === lower) return id;
  }

  for (const l of arr) {
    const id = safeStr(l?.id).trim();
    if (!id) continue;
    const aliases = Array.isArray(l?.aliases) ? l.aliases : [];
    if (aliases.some(a => safeStr(a).trim().toLowerCase() === lower)) return id;
  }

  if (/^\d+$/.test(raw)) return raw;

  return '';
}

function normalizeVisualStyleForApi(v) {
  const s = String(v ?? '').trim().toLowerCase();
  if (!s) return 'shape_label';

  const map = {
    icon_only: 'icon_only',
    text_only: 'icon_only',
    label_only: 'icon_only',

    icon_text: 'icon_label',
    icon_label: 'icon_label',

    icon_label_always: 'icon_label_always',
    icon_always: 'icon_label_always',
    icon_label_persistent: 'icon_label_always',

    shape_only: 'shape_only',

    shape_text: 'shape_label',
    shape_label: 'shape_label',

    number: 'number',
    numeric: 'number',

    number_label: 'number_label',
    number_hover: 'number_label',
    number_mouseover: 'number_label',

    auto: 'Shape Only - Label on Mouseover',
  };

  return map[s] || 'shape_label';
}

function clampInt(n, min, max, fallback) {
  const v = typeof n === 'number' ? n : parseInt(String(n ?? ''), 10);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(min, Math.min(max, v));
}

export default function EditPOI({
  poi,
  customCategories = [],
  customTypes = [],
  userId,
  mapOwnerId,
  onCategoryUpdate,
  canEdit,
  onClose,
  mapId,
  overlayOptions = [],
  activeOverlayId = null,
  onStartMovePoi,
}) {
  const effectiveMapId = Number(poi?.map_id ?? mapId ?? 0) || 0;

  const effectiveUserId = useMemo(() => {
    const candidates = [];
    if (userId != null && userId !== 0) candidates.push(userId);
    if (mapOwnerId != null && mapOwnerId !== 0) candidates.push(mapOwnerId);
    if (poi && poi.created_by != null && poi.created_by !== 0) candidates.push(poi.created_by);
    return candidates.length ? Number(candidates[0]) : null;
  }, [userId, mapOwnerId, poi]);

  const categoriesById = useMemo(() => {
    if (!Array.isArray(customCategories)) return new Map();
    const m = new Map();
    for (const c of customCategories) {
      if (c && c.id != null) m.set(String(c.id), c.name);
    }
    return m;
  }, [customCategories]);

  const typesArray = useMemo(() => {
    if (Array.isArray(customTypes)) {
      return customTypes.map(t => {
        const rawColor =
          t.color != null
            ? t.color
            : t.colour != null
            ? t.colour
            : t.typeColour != null
            ? t.typeColour
            : undefined;
        const rawLabelBg =
          t.label_bg_colour != null ? t.label_bg_colour : t.labelBgColour != null ? t.labelBgColour : undefined;

        return {
          id: t.id,
          name: t.name,
          category_id: t.category_id,
          categoryName: t.category_id != null ? categoriesById.get(String(t.category_id)) : t.categoryName,
          color: rawColor,
          icon: t.icon,
          icon_file: t.icon_file,
          label_border: t.label_border,
          label_bg_colour: rawLabelBg,
        };
      });
    }
    if (customTypes && typeof customTypes === 'object') {
      const out = [];
      for (const [catName, list] of Object.entries(customTypes)) {
        (list || []).forEach(name => out.push({ name, categoryName: catName }));
      }
      return out;
    }
    return [];
  }, [customTypes, categoriesById]);

  const findType = idOrObj => {
    if (idOrObj && typeof idOrObj === 'object') return idOrObj;
    const byId = typesArray.find(t => String(t.id) === String(idOrObj));
    if (byId) return byId;
    const byName = typesArray.find(t => String(t.name) === String(idOrObj));
    return byName || null;
  };

  const findCategory = idOrObjOrName => {
    if (!idOrObjOrName) return null;
    if (typeof idOrObjOrName === 'object') return idOrObjOrName;
    const byId = Array.isArray(customCategories)
      ? customCategories.find(c => String(c.id) === String(idOrObjOrName))
      : null;
    if (byId) return byId;
    return Array.isArray(customCategories)
      ? customCategories.find(c => String(c.name) === String(idOrObjOrName))
      : { name: String(idOrObjOrName) };
  };

  const seedFromPoi = () => {
    const t = findType(poi.type_id ?? poi.type);
    const catNameViaType =
      t?.categoryName ?? (t?.category_id != null ? categoriesById.get(String(t.category_id)) : undefined);
    const c = findCategory(poi.category_id ?? catNameViaType ?? poi.category);
    const labelBorderRaw = poi.label_border ?? t?.label_border;
    const labelBorderNum =
      typeof labelBorderRaw === 'undefined' || labelBorderRaw === null ? 1 : Number(labelBorderRaw);

    const sourceColor =
      t?.color != null
        ? t.color
        : poi.colour != null
        ? poi.colour
        : poi.color != null
        ? poi.color
        : '#ff0000';

    const sourceLabelBg =
      poi.label_bg_colour != null ? poi.label_bg_colour : t?.label_bg_colour != null ? t.label_bg_colour : '#000000';

    let useTransparentBg = false;
    let labelBgColourValue;

    if (typeof sourceLabelBg === 'string') {
      const lower = sourceLabelBg.trim().toLowerCase();
      if (lower === 'transparent' || lower === 'none') {
        useTransparentBg = true;
        labelBgColourValue = '#000000';
      } else {
        labelBgColourValue = padHexColour(sourceLabelBg);
      }
    } else {
      labelBgColourValue = padHexColour(sourceLabelBg);
    }

    const rawVisual =
      poi.visual_style ?? poi.visualStyle ?? poi.marker_style ?? poi.markerStyle ?? poi.visualStyleKey ?? null;
    const visualStyle = normalizeVisualStyleForApi(rawVisual || 'shape_label');

    const rawMarkerShape =
      poi.marker_shape ??
      poi.markerShape ??
      poi.badge_shape ??
      poi.badgeShape ??
      poi.shape ??
      poi.marker ??
      null;

    const markerShapeSeed = String(rawMarkerShape || t?.icon || t?.shape || poi.shape || 'circle')
      .trim()
      .toLowerCase();

    const rawNumberValue = poi.number_value ?? poi.numberValue ?? poi.badge_number ?? poi.badgeNumber ?? null;
    const numberValue = clampInt(rawNumberValue, 1, 125, 1);

    const rawNumberBadgeShape = poi.badge_shape ?? poi.badgeShape ?? poi.number_badge_shape ?? null;
    const numberBadgeShape = String(rawNumberBadgeShape || markerShapeSeed || 'circle')
      .trim()
      .toLowerCase();

    const rawColourOverride =
      poi.colour_override ??
      poi.colourOverride ??
      poi.color_override ??
      poi.colorOverride ??
      poi.colour ??
      null;

    const hasColourOverride =
      poi.colour_override != null ||
      poi.colourOverride != null ||
      poi.color_override != null ||
      poi.colorOverride != null;

    const colourOverride = hasColourOverride ? padHexColour(rawColourOverride) : padHexColour(sourceColor);

    const rawLayerKey = poi.layer_key ?? poi.layerKey ?? null;
    const hasLayerKey = rawLayerKey != null && String(rawLayerKey).trim() !== '';
    const layerKey = hasLayerKey ? String(rawLayerKey).trim() : '';

    return {
      poiName: poi.poi_name || '',
      category: c?.name || (typeof poi.category === 'string' ? poi.category : ''),
      typeName: t?.name || (typeof poi.type === 'string' ? poi.type : ''),
      typeColour: padHexColour(sourceColor),
      typeShape: t?.icon || t?.shape || poi.shape || 'circle',
      labelBorder: labelBorderNum,
      labelBgColour: labelBgColourValue,
      useTransparentBg,
      description: poi.description || '',
      imageUrl: poi.image_url || '',
      iconUrl: poi.icon_file || t?.icon_file || '',
      linkedMapId: poi.linked_map_id || '',

      visualStyle,
      markerShape: markerShapeSeed,
      numberValue,
      numberBadgeShape,
      usePoiColourOverride: !!hasColourOverride,
      poiColour: padHexColour(colourOverride),
      lockPoiLayer: !!hasLayerKey,
      poiLayerKey: layerKey,
    };
  };

  const [mode, setMode] = useState('main');
  const [edit, setEdit] = useState(seedFromPoi());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [typeChangedFlags, setTypeChangedFlags] = useState({
    name: false,
    color: false,
    shape: false,
    labelBorder: false,
    labelBgColour: false,
    icon: false,
  });

  const prevPoiId = useRef();

  const [imageFile, setImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const [imageMsg, setImageMsg] = useState('');

  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveMode, setMoveMode] = useState('existing');
  const [moveExistingOptions, setMoveExistingOptions] = useState([]);
  const [moveTargetExisting, setMoveTargetExisting] = useState('');
  const [moveTargetNew, setMoveTargetNew] = useState('');
  const [moveableTypes, setMoveableTypes] = useState([]);
  const [selectedOtherTypeIds, setSelectedOtherTypeIds] = useState(new Set());

  const [typeMoveModalOpen, setTypeMoveModalOpen] = useState(false);
  const [typeMoveMode, setTypeMoveMode] = useState('existing');
  const [typeMoveTargetExistingId, setTypeMoveTargetExistingId] = useState('');
  const [typeMoveTargetNewName, setTypeMoveTargetNewName] = useState('');
  const [typeMoveTypeOptions, setTypeMoveTypeOptions] = useState([]);
  const [typeMoveablePois, setTypeMoveablePois] = useState([]);
  const [selectedOtherPoiIds, setSelectedOtherPoiIds] = useState(new Set());

  const [lockOverlay, setLockOverlay] = useState(() => poi.overlay_id != null);
  const [selectedOverlayId, setSelectedOverlayId] = useState(() => {
    const raw = poi.overlay_id;
    if (raw === null || raw === undefined) {
      if (activeOverlayId != null && activeOverlayId !== '') {
        return String(activeOverlayId);
      }
      return 'base';
    }
    if (Number(raw) === 1) return 'base';
    return String(raw);
  });

  const [allowIndividualPoiColours, setAllowIndividualPoiColours] = useState(false);
  const [availablePoiLayers, setAvailablePoiLayers] = useState([]);

  const handleStartMovePoi = () => {
    try {
      window.dispatchEvent(
        new CustomEvent('pa-poi-move-start', {
          detail: { poi },
        }),
      );
    } catch {}

    try {
      if (typeof onStartMovePoi === 'function') onStartMovePoi(poi);
    } catch {}

    try {
      if (typeof onClose === 'function') onClose();
    } catch {}
  };

  const handleChange = (field, value) => {
    setEdit(prev => ({ ...prev, [field]: value }));
    setDirty(true);
    if (field === 'typeName') setTypeChangedFlags(prev => ({ ...prev, name: true }));
    else if (field === 'typeColour') setTypeChangedFlags(prev => ({ ...prev, color: true }));
    else if (field === 'typeShape') setTypeChangedFlags(prev => ({ ...prev, shape: true }));
    else if (field === 'labelBorder') setTypeChangedFlags(prev => ({ ...prev, labelBorder: true }));
    else if (field === 'labelBgColour' || field === 'useTransparentBg')
      setTypeChangedFlags(prev => ({ ...prev, labelBgColour: true }));
    else if (field === 'iconUrl') setTypeChangedFlags(prev => ({ ...prev, icon: true }));
  };

  useEffect(() => {
    if (!poi || !poi.id) return;
    if (poi.id !== prevPoiId.current) {
      setEdit(seedFromPoi());
      setDirty(false);
      setSaving(false);
      setTypeChangedFlags({
        name: false,
        color: false,
        shape: false,
        labelBorder: false,
        labelBgColour: false,
        icon: false,
      });

      const raw = poi.overlay_id;
      const nextLock = raw != null;
      setLockOverlay(nextLock);

      let nextSelected;
      if (raw === null || raw === undefined) {
        if (activeOverlayId != null && activeOverlayId !== '') {
          nextSelected = String(activeOverlayId);
        } else {
          nextSelected = 'base';
        }
      } else if (Number(raw) === 1) {
        nextSelected = 'base';
      } else {
        nextSelected = String(raw);
      }
      setSelectedOverlayId(nextSelected);

      setImageFile(null);
      setImageUploading(false);
      setImageError('');
      setImageMsg('');
      setMode('main');
    }
    prevPoiId.current = poi.id;
  }, [poi?.id, activeOverlayId]);

  useEffect(() => {
    const hasIcon = !!(edit.iconUrl && String(edit.iconUrl).trim());
    const vs = normalizeVisualStyleForApi(edit.visualStyle);

    const needsIcon =
      vs === 'icon_only' || vs === 'icon_label' || vs === 'icon_label_always';

    if (!hasIcon && needsIcon) {
      setEdit(prev => ({ ...prev, visualStyle: 'shape_label' }));
      setDirty(true);
    }
  }, [edit.iconUrl, edit.visualStyle]);

  useEffect(() => {
    let cancelled = false;

    async function loadPoiRules() {
      if (!effectiveMapId) {
        setAllowIndividualPoiColours(false);
        setAvailablePoiLayers([]);
        return;
      }
      try {
        const row = await fetchJSON(`/api/mapStyles/${Number(effectiveMapId)}`);
        if (cancelled) return;

        const enabled = Number(row?.poi_allow_individual_colours) === 1;
        setAllowIndividualPoiColours(enabled);

        const layers = normalizePoiLayersFromStylesRow(row);
        setAvailablePoiLayers(layers);

        const rawLayerKey = edit.lockPoiLayer ? String(edit.poiLayerKey || '').trim() : '';
        const resolved = rawLayerKey ? resolveLayerIdFromValue(rawLayerKey, layers) : '';

        if (edit.lockPoiLayer && rawLayerKey && resolved && resolved !== rawLayerKey) {
          setEdit(prev => ({ ...prev, poiLayerKey: resolved }));
        }
      } catch {
        if (cancelled) return;
        setAllowIndividualPoiColours(false);
        setAvailablePoiLayers([]);
      }
    }

    loadPoiRules();
    return () => {
      cancelled = true;
    };
  }, [effectiveMapId]);

  const getCurrentCategoryName = () => {
    const poiTypeObj = typesArray.find(t => String(t.id) === String(poi.type_id));
    const cname =
      poiTypeObj?.categoryName ??
      (poiTypeObj?.category_id != null ? categoriesById.get(String(poiTypeObj.category_id)) : '') ??
      '';
    return String(cname || '').trim();
  };

  const fetchTypeRow = async typeId => {
    const row = await fetchJSON(`/api/type-edit/${typeId}`);
    return {
      id: Number(row.id),
      name: String(row.name || '').trim() || 'Unnamed',
      color: typeof row.color === 'number' ? row.color : hexToInt(row.color || '#ff0000'),
      icon: (row.icon && String(row.icon)) || 'circle',
      category_id: Number(row.category_id || 0),
      map_id: Number(row.map_id || effectiveMapId || 0),
      label_border: Number(row.label_border ?? 1),
      label_bg_colour: row.label_bg_colour ? row.label_bg_colour : '#000000',
      icon_file: row.icon_file || null,
    };
  };

  const buildCategoryNameList = async currentCategoryName => {
    const names = new Set();
    try {
      if (effectiveMapId) {
        const catsResp = await fetchJSON(`/api/poi/categories/${effectiveMapId}`);
        (catsResp?.categories || []).forEach(c => {
          if (c?.name) names.add(String(c.name).trim());
        });
      }
    } catch {}
    if (Array.isArray(customCategories)) {
      customCategories.forEach(c => {
        if (c?.name) names.add(String(c.name).trim());
      });
    }
    typesArray.forEach(t => {
      if (t?.categoryName) names.add(String(t.categoryName).trim());
    });
    if (currentCategoryName) names.add(String(currentCategoryName).trim());
    if (edit.category) names.add(String(edit.category).trim());
    return Array.from(names)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  };

  const getAllPoisForMap = async () => {
    if (!effectiveMapId) return [];
    const data = await fetchJSON(`/api/poi/all/${effectiveMapId}`);
    return Array.isArray(data?.pois) ? data.pois : [];
  };

  const getPOIsForType = async typeId => {
    const all = await getAllPoisForMap();
    return all.filter(p => String(p.type_id) === String(typeId));
  };

  const openMoveModal = async () => {
    if (!effectiveMapId || !effectiveUserId) {
      alert('Missing map/user info, cannot move categories.');
      return;
    }
    try {
      const baseType = await fetchTypeRow(poi.type_id);
      const currentCategoryId = Number(baseType.category_id);
      const currentCategoryName = getCurrentCategoryName();
      let others = [];
      if (Number.isFinite(currentCategoryId) && currentCategoryId > 0) {
        const usage = await fetchJSON(`/api/category-edit/check-category-usage/${currentCategoryId}`);
        const list = Array.isArray(usage?.types) ? usage.types : [];
        others = list
          .map(r => ({
            id: Number(r.type_id),
            name: r.type_name,
            poi_count: Number(r.poi_count || 0),
          }))
          .filter(t => String(t.id) !== String(poi.type_id));
      }
      setMoveableTypes(others);
      setSelectedOtherTypeIds(new Set());
      setMoveMode('existing');
      const options = await buildCategoryNameList(currentCategoryName);
      setMoveExistingOptions(options);
      const firstDifferent =
        options.find(n => n.toLowerCase() !== String(currentCategoryName).toLowerCase()) || '';
      setMoveTargetExisting(firstDifferent);
      setMoveTargetNew('');
      setMoveModalOpen(true);
    } catch (err) {
      alert(err?.message || 'Failed to open move dialog.');
    }
  };

  const ensurePoiCategoryId = async (poiId, categoryName = null, newCategoryId = null) => {
    if (!effectiveMapId || !effectiveUserId) {
      throw new Error('Missing map_id or user_id when updating POI category');
    }
    let finalCategoryName = categoryName;
    if (newCategoryId != null && !finalCategoryName) {
      try {
        const catsResp = await fetchJSON(`/api/poi/categories/${effectiveMapId}`);
        const found = (catsResp?.categories || []).find(c => Number(c.id) === Number(newCategoryId));
        finalCategoryName = found?.name || '';
      } catch {}
    }
    await fetchJSON(`/api/category-edit/${poiId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        category: finalCategoryName ? String(finalCategoryName).trim() : '',
        map_id: Number(effectiveMapId),
        user_id: Number(effectiveUserId),
      }),
    });
  };

  const perTypeMoveFallback = async (typeIds, newCategoryId) => {
    for (const tId of typeIds) {
      const t = await fetchTypeRow(tId);
      if (!t.map_id) throw new Error('Missing map_id for type');
      await fetchJSON(`/api/type-edit/${tId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: t.name,
          color: t.color,
          icon: t.icon,
          category_id: Number(newCategoryId),
          map_id: Number(t.map_id),
          label_border: Number(t.label_border ?? 1),
          label_bg_colour: t.label_bg_colour || '#000000',
          icon_file: t.icon_file || null,
        }),
      });
      const poisForType = await getPOIsForType(tId);
      if (Array.isArray(poisForType)) {
        for (const p of poisForType) {
          await ensurePoiCategoryId(p.id, null, Number(newCategoryId));
        }
      }
    }
  };

  const handleConfirmMove = async () => {
    const target =
      moveMode === 'existing' ? String(moveTargetExisting || '').trim() : String(moveTargetNew || '').trim();

    if (!target) {
      alert('Select or enter a target category.');
      return;
    }
    if (!effectiveMapId || !effectiveUserId) {
      alert('Missing map/user info, cannot move categories.');
      return;
    }

    try {
      const baseType = await fetchTypeRow(poi.type_id);
      const mapIdToUse = Number(effectiveMapId || baseType.map_id);
      const oldCategoryId = Number(baseType.category_id);
      const catsResp = await fetchJSON(`/api/poi/categories/${mapIdToUse}`);
      const cats = catsResp?.categories || [];
      let newCategoryId = cats.find(cat => String(cat.name).trim().toLowerCase() === target.toLowerCase())?.id;
      if (!newCategoryId) {
        const created = await fetchJSON(`/api/poi/category`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            map_id: mapIdToUse,
            name: target,
            user_id: Number(effectiveUserId),
          }),
        });
        newCategoryId = created?.id;
      }
      const selectedIds = Array.from(selectedOtherTypeIds.values()).map(Number);
      const toMove = Array.from(new Set([Number(poi.type_id), ...selectedIds])).filter(Number.isFinite);
      let moved = false;
      if (Number.isFinite(oldCategoryId) && Number.isFinite(newCategoryId) && toMove.length > 0) {
        try {
          await fetchJSON('/api/category-edit/bulk-category-update', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              map_id: mapIdToUse,
              oldCategoryId,
              newCategoryId,
              typeIds: toMove,
            }),
          });
          moved = true;
        } catch {
          await perTypeMoveFallback(toMove, newCategoryId);
          moved = true;
        }
      }
      if (!moved) throw new Error('Unable to resolve categories or types to move.');
      setEdit(prev => ({ ...prev, category: target }));
      await savePOIChanges({ skipCategoryChange: true });
      setMoveModalOpen(false);
      onCategoryUpdate && onCategoryUpdate(poi.id, target, true);
    } catch (err) {
      alert(`Failed to move types: ${err?.message || 'Unknown error'}`);
      setMoveModalOpen(false);
    }
  };

  const handleToggleOtherType = id => {
    setSelectedOtherTypeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openTypeMoveModal = async () => {
    if (!effectiveMapId) {
      alert('Missing map info, cannot move types.');
      return;
    }
    try {
      const allPois = await getAllPoisForMap();
      const baseTypeId = Number(poi.type_id);
      const poisWithBaseType = allPois.filter(p => Number(p.type_id) === baseTypeId);
      const samplePoi = poisWithBaseType[0] || allPois.find(p => Number(p.id) === Number(poi.id)) || null;

      const baseTypeName = samplePoi?.type || '';
      const currentCategoryId = Number(samplePoi?.category_id || 0);
      const currentCategoryName = samplePoi?.category || getCurrentCategoryName();

      const typeMap = new Map();
      allPois.forEach(p => {
        const id = Number(p.type_id);
        if (!Number.isFinite(id)) return;
        if (!typeMap.has(id)) {
          typeMap.set(id, {
            id,
            name: p.type || '',
            category_id: Number(p.category_id || 0),
            categoryName: p.category || '',
          });
        }
      });

      let targetTypes = [];
      if (Number.isFinite(currentCategoryId) && currentCategoryId > 0) {
        targetTypes = Array.from(typeMap.values())
          .filter(t => t.category_id === currentCategoryId && t.id !== baseTypeId)
          .map(t => ({
            id: t.id,
            label: t.name || `Type ${t.id}`,
          }));
      }
      if (targetTypes.length === 0) {
        targetTypes = Array.from(typeMap.values())
          .filter(t => t.id !== baseTypeId)
          .map(t => ({
            id: t.id,
            label: t.categoryName ? `${t.categoryName} - ${t.name || `Type ${t.id}`}` : t.name || `Type ${t.id}`,
          }));
      }

      const otherPoisSameType = poisWithBaseType
        .filter(p => Number(p.id) !== Number(poi.id))
        .map(p => ({
          id: Number(p.id),
          name: p.poi_name || `POI #${p.id}`,
          typeName: currentCategoryName ? `${currentCategoryName} - ${baseTypeName}` : baseTypeName,
        }));

      setTypeMoveTypeOptions(targetTypes);
      setTypeMoveMode('existing');
      setTypeMoveTargetExistingId(targetTypes.length > 0 ? String(targetTypes[0].id) : '');
      setTypeMoveTargetNewName('');
      setTypeMoveablePois(otherPoisSameType);
      setSelectedOtherPoiIds(new Set());
      setTypeMoveModalOpen(true);
    } catch (err) {
      alert(err?.message || 'Failed to open type move dialog.');
    }
  };

  const handleToggleOtherPoi = id => {
    setSelectedOtherPoiIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updatePoiType = async (poiId, newTypeId) => {
    if (!effectiveMapId) throw new Error('Missing map_id for type move');
    await fetchJSON(`/api/poi/update-type/${poiId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type_id: Number(newTypeId),
        map_id: Number(effectiveMapId),
      }),
    });
  };

  const handleConfirmTypeMove = async () => {
    if (!effectiveMapId) {
      alert('Cannot move POIs: missing map_id.');
      return;
    }
    try {
      const baseType = await fetchTypeRow(poi.type_id);
      const mapIdToUse = Number(effectiveMapId || baseType.map_id);
      const oldCategoryId = Number(baseType.category_id);
      let targetTypeIdNum = null;

      if (typeMoveMode === 'existing') {
        if (!typeMoveTargetExistingId) {
          alert('Select a target type.');
          return;
        }
        targetTypeIdNum = Number(typeMoveTargetExistingId);
      } else {
        const newNameTrim = String(typeMoveTargetNewName || '').trim();
        if (!newNameTrim) {
          alert('Enter a name for the new type.');
          return;
        }
        const created = await fetchJSON('/api/poi/type', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: newNameTrim,
            map_id: mapIdToUse,
            category_id: oldCategoryId,
            color: hexToInt(edit.typeColour),
            icon: edit.typeShape || 'circle',
          }),
        });
        targetTypeIdNum = Number(created?.id);
        if (!Number.isFinite(targetTypeIdNum)) {
          throw new Error('Failed to create new type.');
        }
      }

      if (!Number.isFinite(targetTypeIdNum)) {
        throw new Error('Invalid target type.');
      }

      const idsToMove = Array.from(
        new Set([Number(poi.id), ...Array.from(selectedOtherPoiIds.values()).map(Number)]),
      ).filter(Number.isFinite);

      for (const id of idsToMove) {
        await updatePoiType(id, targetTypeIdNum);
      }

      const t = await fetchTypeRow(targetTypeIdNum);
      const lowerBg = typeof t.label_bg_colour === 'string' ? t.label_bg_colour.trim().toLowerCase() : '';

      const transparentFromType = lowerBg === 'transparent' || lowerBg === 'none';

      setEdit(prev => ({
        ...prev,
        typeName: t.name,
        typeColour: padHexColour(t.color),
        typeShape: t.icon || 'circle',
        labelBorder: Number(t.label_border ?? prev.labelBorder ?? 1),
        labelBgColour: transparentFromType ? '#000000' : padHexColour(t.label_bg_colour || prev.labelBgColour || '#000000'),
        useTransparentBg: transparentFromType,
      }));
      setTypeChangedFlags(prev => ({
        ...prev,
        name: true,
        color: true,
        shape: true,
        labelBgColour: true,
        labelBorder: true,
      }));
      setTypeMoveModalOpen(false);
      setDirty(true);
      onCategoryUpdate && onCategoryUpdate(poi.id, edit.category, true);
    } catch (err) {
      alert(err?.message || 'Failed to move POIs to new type.');
      setTypeMoveModalOpen(false);
    }
  };

  const handleImageUpload = async file => {
    setImageError('');
    setImageMsg('');
    if (!file) return;

    if (!effectiveMapId) {
      setImageError('Missing map_id');
      return;
    }
    if (!poi?.id) {
      setImageError('Missing poi id');
      return;
    }
    if (!effectiveUserId) {
      setImageError('Missing user id');
      return;
    }

    if (!/^image\/(png|jpeg|jpg|gif|webp)$/i.test(file.type)) {
      setImageError('PNG/JPG/GIF/WebP only');
      return;
    }
    if (file.size > 1024 * 1024) {
      setImageError('Max 1MB');
      return;
    }

    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('map_id', String(effectiveMapId));
      formData.append('user_id', String(effectiveUserId));
      formData.append('poi_id', String(poi.id));

      const res = await fetch(`/api/poi/upload-image`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(data?.error || 'Upload failed.');
      }
      if (!data || !data.image_url) {
        throw new Error('No image_url in response.');
      }

      setImageMsg('Image uploaded!');
      setEdit(prev => ({ ...prev, imageUrl: data.image_url }));
      setDirty(true);
      setImageFile(null);
      setMode('main');
      onCategoryUpdate && onCategoryUpdate(poi.id, edit.category);
    } catch (err) {
      setImageError(err?.message || 'Failed to upload image.');
    }
    setImageUploading(false);
  };

  const handleDeleteImage = async () => {
    setImageError('');
    setImageMsg('');
    if (!canEdit) return;
    if (!effectiveMapId) {
      setImageError('Missing map_id');
      return;
    }
    if (!poi?.id) {
      setImageError('Missing poi id');
      return;
    }
    if (!edit.imageUrl) {
      setImageError('No image to delete');
      return;
    }
    if (!window.confirm('Delete this POI image?')) return;

    setImageUploading(true);
    try {
      const res = await fetch(`/api/poi/${poi.id}/image`, {
        method: 'DELETE',
        credentials: 'include',
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(data?.error || 'Delete failed.');
      }
      if (!data || !data.ok) {
        throw new Error(data?.error || 'Delete failed.');
      }

      setEdit(prev => ({ ...prev, imageUrl: '' }));
      setDirty(true);
      setImageMsg('Image deleted.');
      setMode('main');
      onCategoryUpdate && onCategoryUpdate(poi.id, edit.category, true);
    } catch (err) {
      setImageError(err?.message || 'Failed to delete image.');
    }
    setImageUploading(false);
  };

  const persistPoiStyleFields = async () => {
    const visualStyleForApi = normalizeVisualStyleForApi(edit.visualStyle);

    await fetchJSON(`/api/poi/update-visual-style/${poi.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        map_id: Number(effectiveMapId),
        visual_style: visualStyleForApi,
      }),
    });

    if (visualStyleForApi === 'number' || visualStyleForApi === 'number_label') {
      const n = clampInt(edit.numberValue, 1, 125, 1);
      const badgeShape = String(edit.numberBadgeShape || 'circle').trim().toLowerCase() || 'circle';

      await fetchJSON(`/api/poi/update-marker/${poi.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          map_id: Number(effectiveMapId),

          marker_shape: badgeShape,
          markerShape: badgeShape,

          badge_shape: badgeShape,
          badgeShape: badgeShape,

          number_value: n,
          numberValue: n,

          badge_number: n,
          badgeNumber: n,
        }),
      });
    } else {
      const shape = String(edit.markerShape || 'circle').trim().toLowerCase() || 'circle';

      await fetchJSON(`/api/poi/update-marker/${poi.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          map_id: Number(effectiveMapId),

          marker_shape: shape,
          markerShape: shape,

          badge_shape: shape,
          badgeShape: shape,

          number_value: null,
          numberValue: null,

          badge_number: null,
          badgeNumber: null,
        }),
      });
    }

    if (allowIndividualPoiColours) {
      const use = !!edit.usePoiColourOverride;
      const value = use ? padHexColour(edit.poiColour || edit.typeColour || '#ff0000') : null;

      await fetchJSON(`/api/poi/update-colour/${poi.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          map_id: Number(effectiveMapId),

          colour_override: value,
          colourOverride: value,

          color_override: value,
          colorOverride: value,
        }),
      });
    }

    const hasLayersUi = Array.isArray(availablePoiLayers) && availablePoiLayers.length > 0;
    if (hasLayersUi) {
      const lock = !!edit.lockPoiLayer;
      const raw = lock ? String(edit.poiLayerKey || '').trim() : '';
      const resolved = raw ? resolveLayerIdFromValue(raw, availablePoiLayers) : '';
      const layerToSend = lock && resolved ? resolved : null;

      await fetchJSON(`/api/poi/update-layer/${poi.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          map_id: Number(effectiveMapId),
          layer_key: layerToSend,
          layerKey: layerToSend,
        }),
      });
    }
  };

  const savePOIChanges = async ({ skipCategoryChange = false } = {}) => {
    setSaving(true);
    try {
      if (typeof edit.poiName === 'string') {
        await fetchJSON(`/api/poi/update-name/${poi.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ poi_name: edit.poiName, map_id: Number(effectiveMapId) }),
        });
      }
      await fetchJSON(`/api/poi/update-desc/${poi.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: edit.description || '', map_id: Number(effectiveMapId) }),
      });
      await fetchJSON(`/api/poi/update-image/${poi.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: edit.imageUrl || null, map_id: Number(effectiveMapId) }),
      });
      await fetchJSON(`/api/poi/update-linked-map/${poi.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          linked_map_id: edit.linkedMapId ? Number(edit.linkedMapId) : null,
          map_id: Number(effectiveMapId),
        }),
      });

      let overlayIdToPersist = null;
      if (lockOverlay) {
        if (selectedOverlayId === 'base') {
          overlayIdToPersist = 1;
        } else if (selectedOverlayId && selectedOverlayId !== '') {
          overlayIdToPersist = Number(selectedOverlayId);
        } else {
          overlayIdToPersist = null;
        }
      } else {
        overlayIdToPersist = null;
      }

      await fetchJSON(`/api/poi/update-overlay/${poi.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          overlay_id: overlayIdToPersist,
          map_id: Number(effectiveMapId),
        }),
      });

      const originalType = typesArray.find(t => String(t.id) === String(poi.type_id));

      const typeNeedsUpdate =
        poi.type_id != null &&
        (typeChangedFlags.name ||
          typeChangedFlags.color ||
          typeChangedFlags.shape ||
          typeChangedFlags.labelBorder ||
          typeChangedFlags.labelBgColour ||
          typeChangedFlags.icon);

      if (typeNeedsUpdate) {
        let typeRow = null;
        try {
          typeRow = await fetchTypeRow(poi.type_id);
        } catch {}

        const mapIdForType = Number(typeRow?.map_id || effectiveMapId);
        const categoryIdForType = Number(typeRow?.category_id ?? originalType?.category_id ?? 0);

        await fetchJSON(`/api/type-edit/${poi.type_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: edit.typeName || typeRow?.name || originalType?.name || '',
            color: hexToInt(edit.typeColour),
            icon: edit.typeShape,
            category_id: categoryIdForType,
            map_id: mapIdForType,
            label_border: Number(edit.labelBorder),
            label_bg_colour: edit.useTransparentBg ? 'transparent' : padHexColour(edit.labelBgColour),
            icon_file: edit.iconUrl || typeRow?.icon_file || originalType?.icon_file || null,
          }),
        });
      }

      if (!skipCategoryChange) {
        const currentCategoryName = getCurrentCategoryName();
        const newCatTrim = String(edit.category || '').trim();
        if (newCatTrim && newCatTrim.toLowerCase() !== currentCategoryName.toLowerCase()) {
          if (!effectiveUserId) {
            throw new Error('Missing user_id when saving category change');
          }
          await fetchJSON(`/api/category-edit/${poi.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              category: newCatTrim,
              map_id: Number(effectiveMapId),
              user_id: Number(effectiveUserId),
            }),
          });
        }
      }

      await persistPoiStyleFields();

      setDirty(false);
      setSaving(false);
      setMode('main');
      setTypeChangedFlags({
        name: false,
        color: false,
        shape: false,
        labelBgColour: false,
        labelBorder: false,
        icon: false,
      });
      onCategoryUpdate && onCategoryUpdate(poi.id, edit.category);
      if (typeof onClose === 'function') onClose();
    } catch (error) {
      setSaving(false);
      alert(error.message || 'Failed to save changes');
    }
  };

  const handleSave = async () => {
    if (saving) return;
    await savePOIChanges({ skipCategoryChange: false });
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this POI? This action cannot be undone.')) return;
    try {
      await fetchJSON(`/api/poi/${poi.id}`, { method: 'DELETE' });

      if (poi.type_id != null) {
        try {
          await fetchJSON(`/api/type-edit/${poi.type_id}/cleanup-icon`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              map_id: Number(effectiveMapId) || undefined,
            }),
          });
        } catch (e) {
          console.warn('Failed to cleanup type icon after POI delete:', e);
        }
      }

      onCategoryUpdate && onCategoryUpdate(poi.id, edit.category);
      if (typeof onClose === 'function') onClose();
    } catch {
      alert('Failed to delete POI.');
    }
  };

  const labelStyle = { display: 'inline-block', minWidth: 120 };

  return (
    <>
      <EditPOIMoveCategoryModal
        open={moveModalOpen}
        moveMode={moveMode}
        setMoveMode={setMoveMode}
        moveExistingOptions={moveExistingOptions}
        moveTargetExisting={moveTargetExisting}
        setMoveTargetExisting={setMoveTargetExisting}
        moveTargetNew={moveTargetNew}
        setMoveTargetNew={setMoveTargetNew}
        moveableTypes={moveableTypes}
        selectedOtherTypeIds={selectedOtherTypeIds}
        onToggleOtherType={() => {}}
        onConfirm={handleConfirmMove}
        onClose={() => setMoveModalOpen(false)}
      />

      <EditPOIMoveTypeModal
        open={typeMoveModalOpen}
        typeMoveMode={typeMoveMode}
        setTypeMoveMode={setTypeMoveMode}
        typeMoveTargetExistingId={typeMoveTargetExistingId}
        setTypeMoveTargetExistingId={setTypeMoveTargetExistingId}
        typeMoveTargetNewName={typeMoveTargetNewName}
        setTypeMoveTargetNewName={setTypeMoveTargetNewName}
        typeMoveTypeOptions={typeMoveTypeOptions}
        typeMoveablePois={typeMoveablePois}
        selectedOtherPoiIds={selectedOtherPoiIds}
        onToggleOtherPoi={() => {}}
        onConfirm={handleConfirmTypeMove}
        onClose={() => setTypeMoveModalOpen(false)}
      />

      {mode === 'main' && (
        <EditPOIMainForm
          poi={poi}
          canEdit={canEdit}
          onStartMovePoi={handleStartMovePoi}
          onClose={onClose}
          edit={edit}
          overlayOptions={overlayOptions}
          customCategories={customCategories}
          typesArray={typesArray}
          selectedOverlayId={selectedOverlayId}
          setSelectedOverlayId={setSelectedOverlayId}
          lockOverlay={lockOverlay}
          setLockOverlay={setLockOverlay}
          labelStyle={labelStyle}
          openMoveModal={openMoveModal}
          openTypeMoveModal={openTypeMoveModal}
          handleChange={handleChange}
          handleDeleteImage={handleDeleteImage}
          handleSave={handleSave}
          dirty={dirty}
          saving={saving}
          effectiveMapId={effectiveMapId}
          setMode={setMode}
          setImageFile={setImageFile}
          imageUploading={imageUploading}
          setDirty={setDirty}
          handleDelete={handleDelete}
          allowIndividualPoiColours={allowIndividualPoiColours}
          availablePoiLayers={availablePoiLayers}
        />
      )}

      {mode === 'icon-upload' && (
        <IconUpload
          initialIconUrl={edit.iconUrl}
          typeId={poi.type_id}
          mapId={effectiveMapId}
          onIconUploaded={async url => {
            setEdit(prev => ({ ...prev, iconUrl: url }));
            setTypeChangedFlags(prev => ({ ...prev, icon: true }));
            setDirty(true);
            setMode('main');
            onCategoryUpdate && onCategoryUpdate(poi.id, edit.category, true);
          }}
          onCancel={() => setMode('main')}
        />
      )}

      {mode === 'image-upload' && (
        <EditPOIImageUpload
          edit={edit}
          imageFile={imageFile}
          setImageFile={setImageFile}
          imageUploading={imageUploading}
          imageError={imageError}
          imageMsg={imageMsg}
          onUpload={file => {
            if (file) handleImageUpload(file);
          }}
          onCancel={() => {
            setMode('main');
            setImageFile(null);
            setImageError('');
            setImageMsg('');
          }}
        />
      )}
    </>
  );
}
